import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress,
    Grid
} from '@mui/material';
import SHA256 from 'crypto-js/sha256';
import { getTodosFichajesAuditoria, getTrabajadores } from '../../services/apis';
import AutocompleteList from '../../utils/componentes-utiles/AutocompleteList';
import { formatoFechaEs } from '../../utils/utils';

export default function AuditoriaFichajesDialog({ open, onClose }) {

    const [resultados, setResultados] = useState([]);
    const [filtroUsuario, setFiltroUsuario] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadFilters, setLoadFilters] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [opTrabajadores, setOpTrabajadores] = useState([]);
    const [trabajadorSelec, setTrabajadorSelec] = useState([]);

    const [filtros, setFiltros] = useState(() => {
        const today = new Date();
        const monthAgo = new Date();
        monthAgo.setMonth(today.getMonth() - 1);
        return {
            fechaDesde: formatDate(monthAgo),
            fechaHasta: formatDate(today),
        };
    });

    function formatDate(date) {
        return date.toISOString().split('T')[0];
    }

    // 🔐 Genera el mismo hash que PHP
    function generarHashReact(f) {

        const s = (v) => v == null ? "" : String(v);

        const cadena =
            s(f.fichaje_id) +
            s(f.usuario_id) +
            s(f.hora_inicio) +
            s(f.hora_fin) +
            s(f.total) +
            s(f.latitud_inicio) +
            s(f.longitud_inicio) +
            s(f.latitud_fin) +
            s(f.longitud_fin) +            
            s(f.hash_anterior) +
            s(f.id_punto_fichaje_inicio) +
            s(f.id_punto_fichaje_fin) +
            s(f.id_tarjeta_inicio) +
            s(f.id_tarjeta_fin) +
            s(f.id_fichaje_corrector) +
            s(f.fecha_corrector) +
            s(f.id_usuario_corrector);

        return SHA256(cadena).toString();
    }

    const fetchFichajes = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getTodosFichajesAuditoria(
                filtros.fechaDesde,
                filtros.fechaHasta,
                selectedUsers
            );

            if (!response) throw new Error('Error al obtener los fichajes');

            const procesados = response.data.fichajes.map(f => {

                const hash_calculado = generarHashReact(f);
                console.log(hash_calculado, f.hash);

                return {
                    ...f,
                    hash_calculado,
                    valido: hash_calculado == f.hash
                };
            });

            setResultados(procesados);
            setLoadFilters(true);

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    const cargarTrabajadores = async () => {
        try {
            const response = await getTrabajadores();
            const opciones = response.data.map(item => ({
                label: item.nombre,
                value: item.id
            }));
            setOpTrabajadores(opciones);
        } catch (error) {
            console.error("Error al cargar trabajadores", error);
        }
    };

    useEffect(() => {
        if (open) {
            fetchFichajes();
            cargarTrabajadores();
        }
    }, [open]);

    useEffect(() => {
        const ids = trabajadorSelec.map(t => t.value);
        setSelectedUsers(ids);
    }, [trabajadorSelec]);

    const filtrados = resultados.filter(f =>
        f.usuario?.toLowerCase().includes(filtroUsuario.toLowerCase())
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                Auditoría de Fichajes {!loadFilters && <CircularProgress size={20} />}
            </DialogTitle>
            <DialogContent dividers>

                <Grid container spacing={2} alignItems="center" mb={2}>

                    {/* Fecha Desde */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Desde"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filtros.fechaDesde}
                            onChange={(e) =>
                                setFiltros(prev => ({ ...prev, fechaDesde: e.target.value }))
                            }
                        />
                    </Grid>

                    {/* Fecha Hasta */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Hasta"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filtros.fechaHasta}
                            onChange={(e) =>
                                setFiltros(prev => ({ ...prev, fechaHasta: e.target.value }))
                            }
                        />
                    </Grid>

                    {/* Autocomplete Usuarios */}
                    <Grid item xs={12} sm={8} md={8} lg={4} mt={-4.2}>
                        <AutocompleteList
                            array={opTrabajadores}
                            setArray={setTrabajadorSelec}
                            label="Seleccionar usuarios"
                        />
                    </Grid>

                    {/* Botón Filtrar */}
                    <Grid item xs={12} sm={6} md={2} display="flex" justifyContent="center">
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                fetchFichajes();
                                setLoadFilters(false);
                            }}
                        >
                            Filtrar
                        </Button>
                    </Grid>

                </Grid>

                {/* FILTRO NOMBRE */}
                <Grid container mb={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Filtrar por usuario"
                            variant="outlined"
                            fullWidth
                            value={filtroUsuario}
                            onChange={(e) => setFiltroUsuario(e.target.value)}
                        />
                    </Grid>
                </Grid>

                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <TableContainer component={Paper}>
                        <Table size="small" sx={{ minWidth: 1600 }}>
                            <TableHead>
                                <TableRow>

                                    {/* Columna 1: Validez */}
                                    <TableCell><b>Validez</b></TableCell>

                                    <TableCell><b>ID</b></TableCell>
                                    <TableCell><b>Usuario</b></TableCell>
                                    <TableCell><b>Inicio</b></TableCell>
                                    <TableCell><b>Fin</b></TableCell>
                                    <TableCell><b>Total (s)</b></TableCell>

                                    <TableCell><b>Lat. Inicio</b></TableCell>
                                    <TableCell><b>Lon. Inicio</b></TableCell>
                                    <TableCell><b>Lat. Fin</b></TableCell>
                                    <TableCell><b>Lon. Fin</b></TableCell>

                                    <TableCell><b>Observación</b></TableCell>
                                    <TableCell><b>Eliminado</b></TableCell>

                                    <TableCell><b>ID Punto Ini</b></TableCell>
                                    <TableCell><b>ID Punto Fin</b></TableCell>

                                    <TableCell><b>ID Tarjeta Ini</b></TableCell>
                                    <TableCell><b>ID Tarjeta Fin</b></TableCell>

                                    <TableCell><b>ID Fichaje Corrector</b></TableCell>
                                    <TableCell><b>Fecha Corrector</b></TableCell>
                                    <TableCell><b>ID Usuario Corrector</b></TableCell>
                                    <TableCell><b>Corrección Aceptada</b></TableCell>
                                    <TableCell><b>Fecha Corrección</b></TableCell>

                                    <TableCell><b>Hash Anterior</b></TableCell>
                                    <TableCell><b>Hash</b></TableCell>

                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filtrados.map((row) => (
                                    <TableRow
                                        key={row.fichaje_id}
                                        sx={{
                                            backgroundColor: row.valido ? "inherit" : "rgba(255,0,0,0.05)"
                                        }}
                                    >

                                        {/* VALIDEZ */}
                                        <TableCell>
                                            {row.valido ? (
                                                <Chip label="Válido" color="success" size="small" />
                                            ) : (
                                                <Chip label="Alterado" color="error" size="small" />
                                            )}
                                        </TableCell>

                                        <TableCell>{row.fichaje_id}</TableCell>
                                        <TableCell>{row.usuario}</TableCell>

                                        <TableCell>{formatoFechaEs(row.hora_inicio)}</TableCell>
                                        <TableCell>{row.hora_fin ? formatoFechaEs(row.hora_fin) : "Activo"}</TableCell>
                                        <TableCell>{row.total}</TableCell>

                                        <TableCell>{row.latitud_inicio}</TableCell>
                                        <TableCell>{row.longitud_inicio}</TableCell>
                                        <TableCell>{row.latitud_fin}</TableCell>
                                        <TableCell>{row.longitud_fin}</TableCell>

                                        <TableCell>{row.observacion || ""}</TableCell>
                                        <TableCell>{row.eliminado}</TableCell>

                                        <TableCell>{row.id_punto_fichaje_inicio}</TableCell>
                                        <TableCell>{row.id_punto_fichaje_fin}</TableCell>

                                        <TableCell>{row.id_tarjeta_inicio}</TableCell>
                                        <TableCell>{row.id_tarjeta_fin}</TableCell>

                                        <TableCell>{row.id_fichaje_corrector}</TableCell>
                                        <TableCell>{row.fecha_corrector}</TableCell>
                                        <TableCell>{row.id_usuario_corrector}</TableCell>
                                        <TableCell>{row.correccion_aceptada}</TableCell>
                                        <TableCell>{row.fecha_correccion_aceptada}</TableCell>

                                        <TableCell>
                                            <Typography variant="body2" noWrap title={row.hash_anterior}>
                                                {row.hash_anterior ? row.hash_anterior.slice(0, 12) + "..." : ""}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" noWrap title={row.hash}>
                                                {row.hash ? row.hash.slice(0, 12) + "..." : ""}
                                            </Typography>
                                        </TableCell>

                                    </TableRow>
                                ))}
                            </TableBody>

                        </Table>

                    </TableContainer>
                )}

            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
